class ParallaxController {
  constructor() {
    this.elements = [];
    this.isDesktop = window.innerWidth > 768;
    this.imagesLoaded = false;
    this.rafId = null;
    this.lastScrollY = 0;
    this.ticking = false;
    this.windowHeight = window.innerHeight;
    this.init();
  }

  init() {
    if (!this.isDesktop) return;

    this.waitForCriticalImages().then(() => {
      this.findElements();
      this.bindEvents();
      this.update();
    });
  }

  async waitForCriticalImages() {
    // Wait for hero/header images to load before calculating positions
    const heroImages = document.querySelectorAll('.slideshow img, .banner__media img, .image-banner img');
    const imagePromises = Array.from(heroImages).map(img => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise(resolve => {
        const onLoad = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onLoad);
          resolve();
        };

        img.addEventListener('load', onLoad);
        img.addEventListener('error', onLoad);
        // Shorter timeout for faster initialization
        setTimeout(resolve, 2000);
      });
    });

    await Promise.all(imagePromises);
    this.imagesLoaded = true;

    // Preload high-resolution versions for parallax scaling
    this.preloadParallaxImages();
  }

  preloadParallaxImages() {
    const parallaxImages = document.querySelectorAll('.parallax-optimized');
    parallaxImages.forEach(img => {
      // Create a higher resolution preload for smooth parallax
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = img.src;
      preloadLink.fetchpriority = 'low';
      document.head.appendChild(preloadLink);
    });
  }

  findElements() {
    // Find all parallax elements
    const parallaxElements = document.querySelectorAll('.parallax-element');
    
    parallaxElements.forEach(element => {
      try {
        const speed = this.getSpeed(element);
        const rect = element.getBoundingClientRect();
        
        // Only add element if it has valid dimensions
        if (rect.height > 0 && rect.width > 0) {
          this.elements.push({
            element,
            speed,
            initialTop: rect.top + window.scrollY,
            height: rect.height
          });
        }
      } catch (error) {
        console.warn('Error processing parallax element:', error);
      }
    });
  }

  getSpeed(element) {
    if (element.classList.contains('parallax-subtle')) return 0.025;
    if (element.classList.contains('parallax-slow')) return 0.05;
    if (element.classList.contains('parallax-medium')) return 0.1;
    if (element.classList.contains('parallax-fast')) return 0.2;
    return 0.2; // default
  }

  update() {
    if (!this.isDesktop || !this.imagesLoaded || this.ticking) return;

    this.ticking = true;
    this.rafId = requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const scrollDelta = Math.abs(scrollY - this.lastScrollY);

      // Skip update if scroll change is minimal
      if (scrollDelta < 1) {
        this.ticking = false;
        return;
      }

      this.lastScrollY = scrollY;
      const windowHeight = this.windowHeight;

      this.elements.forEach(item => {
        const { element, speed, initialTop, height } = item;

        // Optimized viewport checking with margin
        const elementTop = initialTop - scrollY;
        const elementBottom = elementTop + height;
        const margin = 100; // Add margin for smoother transitions

        if (elementBottom >= -margin && elementTop <= windowHeight + margin) {
          // Use translate3d for GPU acceleration
          const offset = Math.round((scrollY - initialTop) * speed);
          const maxOffset = height * 0.2;
          const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, offset));

          element.style.transform = `translate3d(0, ${clampedOffset}px, 0)`;

          if (!element.classList.contains('animating')) {
            element.classList.add('animating');
          }

          // Apply scale to images with GPU acceleration
          const img = element.querySelector('img, video');
          if (img) {
            const scale = element.classList.contains('parallax-subtle') ? '1.03' : '1.15';
            img.style.transform = `scale3d(${scale}, ${scale}, 1)`;
          }
        } else {
          if (element.classList.contains('animating')) {
            element.classList.remove('animating');
            // Reset transform for off-screen elements to save GPU memory
            element.style.transform = 'translate3d(0, 0, 0)';
            const img = element.querySelector('img, video');
            if (img) {
              const scale = element.classList.contains('parallax-subtle') ? '1.03' : '1.15';
              img.style.transform = `scale3d(${scale}, ${scale}, 1)`;
            }
          }
        }
      });

      this.ticking = false;
    });
  }

  bindEvents() {
    // Use Lenis scroll event if available, otherwise use window scroll
    if (window.lenisScroll && window.lenisScroll.lenis) {
      window.lenisScroll.lenis.on('scroll', () => {
        this.update();
      });
    } else {
      window.addEventListener('scroll', () => {
        requestAnimationFrame(() => this.update());
      });
    }

    // Enhanced resize handler
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.windowHeight = window.innerHeight;
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth > 768;

        if (this.isDesktop && !wasDesktop) {
          // Switched from mobile to desktop
          this.init();
        } else if (!this.isDesktop && wasDesktop) {
          // Switched from desktop to mobile - cleanup
          this.cleanup();
        } else if (this.isDesktop) {
          // Still desktop, just recalculate positions
          this.elements = [];
          this.findElements();
        }
      }, 250);
    });
  }

  cleanup() {
    // Cancel any pending animation frame
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Remove transforms and animating classes
    this.elements.forEach(item => {
      item.element.style.transform = '';
      item.element.classList.remove('animating');
      const img = item.element.querySelector('img, video');
      if (img) {
        img.style.transform = '';
      }
    });
    this.elements = [];
    this.ticking = false;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ParallaxController();
  });
} else {
  new ParallaxController();
}